import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar"
import { ThemeToggle } from "@/components/common/ThemeToggle"
import { Credits } from "@/components/subscription/Credits"
import { SubscriptionPaywall } from "@/components/subscription/SubscriptionPaywall"
import { User } from "@/components/auth/User";
import { useAuth, useProfile } from "@/hooks";
import { useApp } from "@/contexts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Settings, Edit3, FolderOpen, Lightbulb, MessageSquare, Dna, LogOut, CalendarDays, LifeBuoy, Home } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useState } from "react";
import Logo from "@/components/common/Logo";
import { usePlanNotifications } from "@/hooks/usePlanNotifications";
import { Separator } from "../ui/separator";

interface AppSidebarProps {
  onFeedbackClick?: () => void;
}

export function AppSidebar({ onFeedbackClick }: AppSidebarProps = {}) {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const {
    selectedBrandId,
    brands,
    setIsCreateContentOpen
  } = useApp();
  const [showPaywall, setShowPaywall] = useState(false);
  const { unreadCount: unreadPlanCount } = usePlanNotifications();

  // Only show upgrade button for free tier users
  const showUpgradeButton = profile?.subscription_tier === 'free';
  const isSignedIn = !!user;

  const selectedBrand = brands.find(brand => brand.id === selectedBrandId);

  const isActive = (path: string) => location.pathname === path;

  const handleLinkClick = () => {
    // Close the sidebar on mobile when a link is clicked
    setOpenMobile(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setOpenMobile(false);
  };

  return (
    <Sidebar variant="inset" collapsible="icon" className="glass ml-4 lg:h-[calc(100vh-2rem)] lg:my-auto rounded-lg border-glass">
      <SidebarHeader>
        <div className="flex items-center p-2 pr-0 justify-between">
          <div className="flex items-center gap-2">
            <Logo className="h-7 w-auto" />
            <span className="sr-only">Arcitext</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {isSignedIn ? (
          <>
            {/* Navigation */}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app')}>
                      <Link to="/app" onClick={handleLinkClick}>
                        <Home className="w-4 h-4" />
                        Overview
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Write</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/writing-styles')}>
                      <Link to="/app/writing-styles" onClick={handleLinkClick}>
                        <Dna className="w-4 h-4" />
                        Writing Styles
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/write')}>
                      <Link to="/app/write" onClick={handleLinkClick}>
                        <Edit3 className="w-4 h-4" />
                        Write
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/content')}>
                      <Link to="/app/content" onClick={handleLinkClick}>
                        <FolderOpen className="w-4 h-4" />
                        Documents
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Tools & Inspiration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/chat')}>
                      <Link to="/app/chat" onClick={handleLinkClick}>
                        <MessageSquare className="w-4 h-4" />
                        Chat
                        <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">New</Badge>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/discover')}>
                      <Link to="/app/discover" onClick={handleLinkClick}>
                        <Lightbulb className="w-4 h-4" />
                        Discover
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname.startsWith('/app/schedule')}>
                      <Link to="/app/schedule" onClick={handleLinkClick}>
                        <CalendarDays className="w-4 h-4" />
                        Schedule
                        {unreadPlanCount > 0 && (
                          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                            {unreadPlanCount}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Settings</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/settings')}>
                      <Link to="/app/settings" onClick={handleLinkClick}>
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/app/help')}>
                      <Link to="/app/help" onClick={handleLinkClick}>
                        <LifeBuoy className="w-4 h-4" />
                        Help & Tutorials
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        ) : (
          <SidebarGroup>
            <SidebarGroupLabel>Explore</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive('/')}>
                    <Link to="/" onClick={handleLinkClick}>
                      <MessageSquare className="w-4 h-4" />
                      Chat
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive('/features')}>
                    <Link to="/features" onClick={handleLinkClick}>
                      <Lightbulb className="w-4 h-4" />
                      Features
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive('/pricing')}>
                    <Link to="/pricing" onClick={handleLinkClick}>
                      <Dna className="w-4 h-4" />
                      Pricing
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        {/* Credits display */}
        {user && (
          <SidebarGroup>
            <SidebarGroupContent className="flex flex-col gap-2">
              {isSignedIn && (
                <div className="px-3">
                  <User />
                </div>
              )}
              <Credits />
              {showUpgradeButton && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowPaywall(true)}
                  title="Upgrade Plan"
                  className="w-full"
                >
                  Upgrade
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="hidden md:flex w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </Button>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Feedback button - visible on mobile only */}
        {onFeedbackClick && (
          <div className="p-2 md:hidden space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onFeedbackClick}
              className="w-full"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        )}
      </SidebarFooter>

      {/* Upgrade Paywall Dialog */}
      <SubscriptionPaywall
        open={showPaywall}
        onOpenChange={setShowPaywall}
      // reason="upgrade_button_sidebar"
      />
    </Sidebar>
  )
}
